import { Classes } from "@blueprintjs/core";
import { Classes as PopoverClasses } from "@blueprintjs/popover2";
import { CommentableEntity, OrgRole } from "@hex/common";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";

import {
  HexButton,
  HexNonIdealState,
  HexPopover,
  HexTooltip,
} from "../../hex-components";
import { useEntityCommentsSelector } from "../../hex-version-multiplayer/state-hooks/entityCommentsStateHooks";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { usePrevious } from "../../hooks/usePrevious";
import { useToggleState } from "../../hooks/useToggleState";
import { useDispatch, useSelector } from "../../redux/hooks";
import { setFocusedCommentId } from "../../redux/slices/logicViewSlice";
import { customScrollIntoView } from "../../util/customScrollIntoView";
import { CyData } from "../../util/cypress";
import { useProjectContext } from "../../util/projectContext";
import { useHexFlags } from "../../util/useHexFlags";
import { ScrollPin } from "../common/ScrollPin";
import {
  AddIcon,
  CommentBubbleIcon,
  CommentBubbleSmallIcon,
} from "../icons/CustomIcons";

import { CommentViewLocation } from "./Comment";
import { CommentEditor } from "./CommentEditor";
import { CommentThread } from "./CommentThread";

const CellBadgeButton = styled.div`
  position: relative;

  display: flex;

  cursor: pointer;
`;

const Divider = styled.hr`
  width: 100%;
  height: 1px;
  margin: 0;

  background-color: ${({ theme }) => theme.borderColor.MUTED};
  border: none;
`;

const CommentButton = styled(HexButton)`
  &&&&&& {
    margin: 0;
    padding: 0 2px;
  }
`;

const CellBadgeInner = styled.div`
  && {
    position: relative;

    display: flex;
    align-items: center;

    .${Classes.ICON} {
      margin: 0;
    }
  }
`;

const CellBadgeButtonText = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 16px;
  height: 16px;
  padding-bottom: 3px;

  color: ${({ theme }) => theme.userActionColor};
  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
  font-size: 10px;
  line-height: 15px;
`;

const CellBadgeButtonOffsetText = styled.span`
  margin: 0 1px 0 2px;
  padding-bottom: 1px;

  color: ${({ theme }) => theme.userActionColor};
  line-height: 15px;
`;

const StyledCommentBubbleIcon = styled(CommentBubbleIcon)<{ faded: boolean }>`
  ${({ faded }) => faded && "opacity: 0.5;"}
  color: ${({ theme }) => theme.userActionColor};
`;

const StyledCommentBubbleSmallIcon = styled(CommentBubbleSmallIcon)<{
  $hasComments: boolean;
}>`
  &&&.${Classes.ICON} {
    > svg {
      color: ${({ $hasComments, theme }) =>
        $hasComments ? theme.userActionColor : theme.iconColor};

      path {
        fill-opacity: ${({ $hasComments }) => ($hasComments ? "0.2" : "0")};
      }
    }
  }
`;

const CommentPopover = styled(HexPopover)<{
  $show: boolean;
}>`
  display: flex;

  transition: opacity ${({ theme }) => theme.animation.duration}
      ${({ theme }) => theme.animation.easing},
    transform ${({ theme }) => theme.animation.duration}
      ${({ theme }) => theme.animation.easing};

  &&&& {
    ${({ $show }) => $show && `opacity: 1; width: auto;`}
  }
`;

const StyledPopover = styled(HexPopover)<{
  hide: "true" | "false";
}>`
  display: flex;
  padding: 2px;

  border-radius: ${({ theme }) => theme.borderRadius};

  transition: opacity ${({ theme }) => theme.animation.duration}
    ${({ theme }) => theme.animation.easing};

  user-select: none;

  &:hover {
    background-color: ${({ theme }) => theme.hoverColor};

    ${StyledCommentBubbleIcon} {
      opacity: 1;
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.activeColor};
  }

  ${({ hide }) => (hide === "true" ? "opacity: 0;" : "")}

  &.${PopoverClasses.POPOVER2_OPEN} {
    opacity: 1;
  }
`;

const CommentScrollContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  padding: 8px;
  overflow-y: auto;
`;

const CellBadgePlusIcon = styled.div`
  position: relative;

  width: 7px;
  height: 7px;

  &::before,
  &::after {
    position: absolute;

    display: block;

    background-color: ${({ theme }) => theme.userActionColor};
    border-radius: 1px;

    content: "";
  }

  &::before {
    top: 3px;
    left: 0;

    width: 100%;
    height: 1px;
  }

  &::after {
    top: 0;
    left: 3px;

    width: 1px;
    height: 100%;
  }
`;

export interface CommentBadgeProps {
  readonly className?: string;
  readonly entityId: string;
  readonly entityType: CommentableEntity;
  readonly canComment: boolean;
  readonly location: CommentViewLocation;
  readonly isFloating?: boolean;
  // Whether the badge can be auto opened on load
  // due to something like a permalink. defaults to true.
  readonly canAutoOpenOnLoad?: boolean;
}

export const CommentBadge: React.ComponentType<CommentBadgeProps> = React.memo(
  function CommentBadge({
    canAutoOpenOnLoad = true,
    canComment,
    className,
    entityId,
    entityType,
    isFloating = true,
    location,
  }) {
    const { "new-project-chrome": newProjectChrome } = useHexFlags();
    const theme = useTheme();
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const focusedCommentId = useSelector(
      (state) => state.logicView.focusedCommentId,
    );
    const { trashed } = useProjectContext();

    const commentsToGet = useMemo(() => {
      if (location === "project") {
        if (newProjectChrome) {
          return "all";
        } else {
          return "project";
        }
      } else {
        return "app";
      }
    }, [location, newProjectChrome]);

    const rawComments = useEntityCommentsSelector({
      commentOrigin: commentsToGet,
      entityId,
      selector: (comments) => comments,
    });

    const comments = useMemo(
      () =>
        rawComments
          .filter((c) => c.deletedDate == null)
          .sort(
            (a, b) =>
              new Date(a.createdDate).getTime() -
              new Date(b.createdDate).getTime(),
          ) ?? [],
      [rawComments],
    );

    const openComments = useMemo(
      () => comments.filter((c) => !c.resolved),
      [comments],
    );

    const resolvedComments = useMemo(
      () => comments.filter((c) => c.resolved),
      [comments],
    );

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    useLayoutEffect(() => {
      const flatCommentIds = comments.flatMap((comment) => [
        comment.id,
        ...comment.childComments.map((c) => c.id),
      ]);
      if (
        focusedCommentId != null &&
        flatCommentIds.includes(focusedCommentId) &&
        canAutoOpenOnLoad === true
      ) {
        setIsPopoverOpen(true);
        // avoid a loop
        dispatch(setFocusedCommentId(null));
      }
    }, [canAutoOpenOnLoad, comments, dispatch, focusedCommentId]);

    const [
      isCommentCreatorOpen,
      setIsCommentCreatorOpen,
      { setFalse: closeCommentCreator, setTrue: openCommentCreator },
    ] = useToggleState(false);

    const [
      isShowingOpenComments,
      ,
      { setFalse: showResolvedComments, setTrue: showOpenComments },
    ] = useToggleState(true);

    const previousOpenLength = usePrevious(openComments.length);
    // scroll to the bottom if we get new comments
    const scrollPinRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
      if (
        scrollPinRef.current != null &&
        (previousOpenLength ?? 0) < openComments.length
      ) {
        customScrollIntoView(scrollPinRef.current, {
          scrollMode: "if-needed",
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [previousOpenLength, openComments.length]);

    // since we hide the resolved tab when there is none
    // make sure the user doesn't get stuck on it
    useEffect(() => {
      if (resolvedComments.length === 0) {
        showOpenComments();
      }
    }, [resolvedComments.length, showOpenComments]);

    const onPopoverInteraction = useCallback(
      (nextOpenState: boolean, evt?: React.SyntheticEvent<HTMLElement>) => {
        evt?.stopPropagation();
        setIsPopoverOpen(nextOpenState);
        if (nextOpenState) {
          setIsCommentCreatorOpen(openComments.length === 0);
        }
      },
      [setIsCommentCreatorOpen, openComments.length],
    );

    const stopPropagation = useCallback(
      (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
      },
      [],
    );

    const commentThreads = useMemo(
      () =>
        (isShowingOpenComments ? openComments : resolvedComments).map(
          (comment) => {
            const { childComments, resolved, ...mainComment } = comment;
            return (
              <CommentThread
                key={mainComment.id}
                appComment={mainComment.appComment}
                canComment={canComment}
                childComments={childComments
                  .filter((child) => child.deletedDate == null)
                  .map((child) => ({
                    ...child,
                    commentId: child.id,
                    createdDate: new Date(child.createdDate),
                    editedTextDate:
                      child.editedTextDate != null
                        ? new Date(child.editedTextDate)
                        : null,
                  }))}
                currentUserId={currentUser?.id}
                isAppCommentInProject={
                  newProjectChrome &&
                  location === "project" &&
                  mainComment.appComment
                }
                mainComment={{
                  ...mainComment,
                  commentId: mainComment.id,
                  createdDate: new Date(mainComment.createdDate),
                  editedTextDate:
                    mainComment.editedTextDate != null
                      ? new Date(mainComment.editedTextDate)
                      : null,
                }}
                resolved={resolved}
              />
            );
          },
        ),
      [
        isShowingOpenComments,
        openComments,
        resolvedComments,
        canComment,
        currentUser?.id,
        newProjectChrome,
        location,
      ],
    );

    const commentEditor = useMemo(
      () =>
        trashed ? null : (
          <>
            {commentThreads.length === 0 && (
              <HexNonIdealState
                $autoHeight={true}
                $small={true}
                description="Be the first to add a comment."
                title="No comments yet"
              />
            )}
            <Divider />
            <div
              css={`
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
              `}
            >
              {isCommentCreatorOpen && currentUser?.id != null ? (
                <div
                  css={`
                    padding: 10px;
                    width: 100%;
                  `}
                >
                  <CommentEditor
                    appComment={location === "app"}
                    creatorId={currentUser.id}
                    entityId={entityId}
                    entityType={entityType}
                    helperText="Add comment"
                    initialRichText={null}
                    onComplete={closeCommentCreator}
                  />
                </div>
              ) : (
                <div
                  css={`
                    padding: 5px;
                    width: 100%;
                  `}
                >
                  <HexButton
                    fill={true}
                    icon={<AddIcon />}
                    minimal={true}
                    onClick={openCommentCreator}
                  >
                    Add comment
                  </HexButton>
                </div>
              )}
            </div>
          </>
        ),
      [
        trashed,
        commentThreads.length,
        isCommentCreatorOpen,
        currentUser?.id,
        location,
        entityId,
        entityType,
        closeCommentCreator,
        openCommentCreator,
      ],
    );

    const popOverContent = useMemo(
      () => (
        <div
          css={`
            display: flex;
            flex-direction: column;
            min-height: 0;
            width: 330px;
            max-height: 40vh;
          `}
          onClick={stopPropagation}
        >
          {resolvedComments.length > 0 && (
            <>
              <div
                css={`
                  width: 100%;
                  display: flex;
                  flex-direction: row;
                  justify-content: center;
                  align-items: center;
                  padding: 8px;
                `}
              >
                <HexButton
                  $extraSmall={true}
                  active={isShowingOpenComments}
                  css={`
                    margin-right: 3px;
                  `}
                  minimal={true}
                  onClick={showOpenComments}
                >
                  Open
                </HexButton>
                <HexButton
                  $extraSmall={true}
                  active={!isShowingOpenComments}
                  minimal={true}
                  onClick={showResolvedComments}
                >
                  Resolved
                </HexButton>
              </div>
              <Divider />
            </>
          )}
          {commentThreads.length > 0 && (
            <CommentScrollContainer>
              {commentThreads}
              <ScrollPin ref={scrollPinRef} />
            </CommentScrollContainer>
          )}
          {canComment &&
            currentUser?.id != null &&
            isShowingOpenComments &&
            commentEditor}
        </div>
      ),
      [
        stopPropagation,
        resolvedComments.length,
        isShowingOpenComments,
        showOpenComments,
        showResolvedComments,
        commentThreads,
        canComment,
        currentUser?.id,
        commentEditor,
      ],
    );

    const popperMods = useMemo(
      () => ({
        flip: { enabled: true },
        preventOverflow: { enabled: true },
      }),
      [],
    );

    // Prevents rendering if the user can't comment and there are no comments to view
    if (openComments.length === 0 && !canComment) {
      return null;
    }

    // Don't show if anonymous user
    if (currentUser?.orgRole === OrgRole.ANONYMOUS) {
      return null;
    }

    if (!isFloating) {
      return (
        <CommentPopover
          // Because of https://github.com/palantir/blueprint/issues/2863
          $show={openComments.length > 0}
          autoFocus={false}
          canEscapeKeyClose={false}
          className={className}
          content={popOverContent}
          isOpen={isPopoverOpen}
          lazy={true}
          modifiers={popperMods}
          placement="left-start"
          onInteraction={onPopoverInteraction}
        >
          <HexTooltip
            content={
              openComments.length > 0 ? "View comments" : "Add a comment"
            }
            placement="bottom"
          >
            <CommentButton
              data-cy={CyData.COMMENT_BADGE}
              minimal={true}
              role="button"
              small={true}
            >
              <CellBadgeInner>
                <StyledCommentBubbleSmallIcon
                  $hasComments={openComments.length > 0}
                />
                {openComments.length > 0 && (
                  <CellBadgeButtonOffsetText>
                    {openComments.length}
                  </CellBadgeButtonOffsetText>
                )}
              </CellBadgeInner>
            </CommentButton>
          </HexTooltip>
        </CommentPopover>
      );
    }

    return (
      <StyledPopover
        // Because of https://github.com/palantir/blueprint/issues/2863
        autoFocus={false}
        className={className}
        content={popOverContent}
        hide={openComments.length > 0 ? "false" : "true"}
        isOpen={isPopoverOpen}
        modifiers={popperMods}
        placement="right-start"
        onInteraction={onPopoverInteraction}
      >
        <CellBadgeButton data-cy={CyData.COMMENT_BADGE} role="button">
          <StyledCommentBubbleIcon
            color={theme.userActionColor}
            faded={openComments.length === 0}
          />
          <CellBadgeButtonText>
            {openComments.length > 0 ? (
              openComments.length
            ) : (
              <CellBadgePlusIcon />
            )}
          </CellBadgeButtonText>
        </CellBadgeButton>
      </StyledPopover>
    );
  },
);
